/**
 * The chat screen's side of "one conversation per customer"
 * (server/support-conversation.js). The server holds what was said, the
 * same on every device, including the team's messages. The device holds
 * what only it saw: flight option cards, a booking card, which specialist
 * a bubble belongs to. These helpers put the two together.
 */

/** How often an open chat checks for a new message from the team. */
export const TEAM_POLL_MS = 20_000;

export const TEAM_AUTHOR_DEFAULT = 'Defera team';

/** Who signs a team bubble, and the picture beside it. */
export const TEAM_AVATARS = Object.freeze({
  'Defera team': '/favicon-192x192.png',
  Stephanie: '/staff/stephanie.png',
});

/** A message from /api/support-chat/conversation as a chat bubble. */
export function bubbleFromServer(m, { specialistFor = null } = {}) {
  const bubble = {
    role: m.role,
    content: String(m.content || ''),
    timestamp: Date.parse(m.createdAt) || Date.now(),
    serverId: m.id,
    seq: Number(m.seq) || null,
  };
  if (m.kind === 'prelude') bubble.kind = 'ack';
  if (m.role === 'assistant' && m.specialistId && specialistFor) {
    const specialist = specialistFor(m.specialistId);
    if (specialist) bubble.specialist = specialist;
  }
  if (m.role === 'team') bubble.authorLabel = m.authorLabel || TEAM_AUTHOR_DEFAULT;
  return bubble;
}

const sameWords = (a, b) => String(a || '').replace(/\s+/g, ' ').trim() === String(b || '').replace(/\s+/g, ' ').trim();

/**
 * The transcript to show after loading the server's copy.
 *
 * - The server's messages, in its order, are the conversation.
 * - A device bubble with the same speaker and words (in order) lends its
 *   cards and specialist to the server's copy of it.
 * - A device bubble newer than the server's last message (and than "New
 *   chat") is a turn still being saved, so it stays, at the end.
 * - Everything else on the device is from before "New chat", another
 *   conversation, or a note that only lived on this screen: dropped.
 *
 * When the server has nothing at all (no messages and no "New chat"), the
 * device's transcript stands.
 */
export function mergeConversation(local, server, { contextResetAt = null } = {}) {
  const device = Array.isArray(local) ? local : [];
  const remote = Array.isArray(server) ? server : [];
  if (!remote.length && !contextResetAt) return device;

  const used = new Set();
  let cursor = 0;
  const merged = remote.map((bubble) => {
    if (bubble.role === 'team') return bubble;
    for (let i = cursor; i < device.length; i += 1) {
      const mine = device[i];
      if (used.has(i) || mine.role !== bubble.role || !sameWords(mine.content, bubble.content)) continue;
      used.add(i);
      cursor = i + 1;
      return {
        ...bubble,
        ...(mine.flightOptions ? { flightOptions: mine.flightOptions } : {}),
        ...(mine.handoff ? { handoff: mine.handoff } : {}),
        ...(mine.specialist ? { specialist: mine.specialist } : {}),
      };
    }
    return bubble;
  });

  const resetAt = Date.parse(contextResetAt) || 0;
  const newestServerAt = Math.max(resetAt, ...remote.map((b) => b.timestamp || 0));
  const unsaved = device.filter((mine, i) => (
    !used.has(i) && mine.role !== 'team' && !mine.serverId && (mine.timestamp || 0) > newestServerAt
  ));
  return [...merged, ...unsaved];
}

/** Team bubbles the screen doesn't have yet, added at the end. */
export function appendTeamBubbles(current, incoming) {
  const list = Array.isArray(current) ? current : [];
  const have = new Set(list.map((b) => b.serverId).filter(Boolean));
  const fresh = (incoming || []).filter((b) => b.role === 'team' && b.serverId && !have.has(b.serverId));
  if (!fresh.length) return list;
  return [...list, ...fresh.sort((a, b) => (a.seq || 0) - (b.seq || 0))];
}

/** Older server messages for "Show earlier messages", ahead of what's on screen. */
export function prependEarlier(current, older) {
  const list = Array.isArray(current) ? current : [];
  const have = new Set(list.map((b) => b.serverId).filter(Boolean));
  const fresh = (older || []).filter((b) => b.serverId && !have.has(b.serverId));
  return fresh.length ? [...fresh, ...list] : list;
}

/** The oldest server message on screen, where "Show earlier messages" pages back from. */
export function oldestSeq(bubbles) {
  const seqs = (bubbles || []).map((b) => b.seq).filter((n) => Number.isFinite(n) && n > 0);
  return seqs.length ? Math.min(...seqs) : null;
}

/** What the desk is sent: the customer's turns and its own, never the team's. */
export function historyForDesk(bubbles) {
  return (bubbles || []).filter((m) => m.role !== 'team').map((m) => ({ role: m.role, content: m.content }));
}

/** "3:48 PM" today, "Oct 21, 3:48 PM" before today. */
export function bubbleTime(timestamp, now = new Date()) {
  const at = new Date(timestamp);
  if (Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (at.toDateString() === now.toDateString()) return time;
  const date = at.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(at.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
  return `${date}, ${time}`;
}
